import React, { Suspense } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import ShiftMonitor from '../Shifts/ShiftMonitor';
import ScrapModal from '../Shifts/ScrapModal';
import { confirm } from '../common/confirm';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';

/* ---- Minimal inline icon set (stroke, currentColor) ---- */
const Icon = ({ name, className = 'w-[18px] h-[18px]' }) => {
    const paths = {
        today: 'M3 10.5L12 4l9 6.5V20a1 1 0 01-1 1h-5v-6H9v6H4a1 1 0 01-1-1v-9.5z',
        material: 'M12 3v4m0 0c-3 0-5 2-5 5v6a2 2 0 002 2h6a2 2 0 002-2v-6c0-3-2-5-5-5zm-5 9h10',
        entry: 'M12 4v16m8-8H4',
        receive: 'M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4',
        shifts: 'M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z',
        stock: 'M20 7L12 3 4 7m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
        category: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
        report: 'M9 17v-6m4 6V7m4 10v-3M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z',
        hr: 'M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4z',
        logout: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
        close: 'M6 18L18 6M6 6l12 12',
        chevron: 'M19 9l-7 7-7-7',
        guide: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
    };
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={paths[name] || ''} />
        </svg>
    );
};

/* ---------------------------------------------------------------------
   Menus by role (Harbor). A tab is either one page or a group (▾) of pages.
   ------------------------------------------------------------------- */
const STOCK_ALL = [
    { path: '/inventory/receive', label: 'Receive stock', hint: 'A vendor delivery arrived' },
    { path: '/inventory/material', label: 'Material entry', hint: 'What went into the machine' },
    { path: '/inventory/ledger', label: 'Stock ledger', hint: 'Every entry, date by date' },
    { path: '/inventory/stock', label: 'Stock on hand', hint: 'What is in the store now' },
    { path: '/inventory/jumbo', label: 'Jumbo and rewinding', hint: 'Make jumbo rolls, rewind them' },
    { path: '/admin/stock-audit', label: 'Stock count', hint: 'Count and fix differences' },
    { path: '/inventory/items', label: 'Items and groups', hint: 'Add and organise materials' },
];

const MENUS = {
    admin: [
        { label: 'Today', icon: 'today', path: '/admin/dashboard' },
        {
            label: 'Production', icon: 'entry', items: [
                { path: '/', label: 'Production entry', hint: 'Log boxes during a shift' },
                { path: '/history', label: 'History', hint: 'Every box, by day and film type' },
                { path: '/shifts', label: 'Shifts', hint: 'Production grouped by shift' },
            ]
        },
        { label: 'Stock', icon: 'stock', items: STOCK_ALL },
        {
            label: 'People', icon: 'hr', items: [
                { path: '/hr/team', label: 'Team', hint: 'Workers, photos and groups' },
                { path: '/hr/attendance', label: 'Attendance', hint: 'Month register, today, holidays' },
                { path: '/hr/payroll', label: 'Payroll', hint: 'Salary, advances, slips' },
            ]
        },
        // One tab: the Setup page has its own menu with every setting in it.
        { label: 'Setup', icon: 'category', path: '/setup' },
        { label: 'Reports', icon: 'report', path: '/inventory/reports' },
    ],
    writer: [
        { label: 'Today', icon: 'today', path: '/today' },
        { label: 'Material', icon: 'material', path: '/inventory/material' },
        { label: 'Receive stock', short: 'Receive', icon: 'receive', path: '/inventory/receive' },
        {
            label: 'Stock', icon: 'stock', items: [
                ...STOCK_ALL.filter(i => i.path !== '/inventory/receive' && i.path !== '/inventory/material'),
                { path: '/admin/bom', label: 'Bill of materials', hint: 'What one box uses up' },
                { path: '/history', label: 'Production history', hint: 'Every box, by day and film type' },
                { path: '/shifts', label: 'Shifts', hint: 'Production grouped by shift' },
            ]
        },
        {
            label: 'People', icon: 'hr', items: [
                { path: '/hr/team', label: 'Team', hint: 'Workers, photos and groups' },
                { path: '/hr/attendance', label: 'Attendance', hint: 'Month register, today, holidays' },
            ]
        },
        { label: 'Reports', icon: 'report', path: '/inventory/reports' },
    ],
    operator: [
        {
            label: 'Production', icon: 'entry', items: [
                { path: '/', label: 'Production entry', hint: 'Log boxes during your shift' },
                { path: '/history', label: 'History', hint: 'Every box, by day and film type' },
                { path: '/shifts', label: 'Shifts', hint: 'Production grouped by shift' },
            ]
        },
        { label: 'Receive stock', short: 'Receive', icon: 'receive', path: '/inventory/receive' },
        {
            label: 'Stock', icon: 'stock', items: [
                { path: '/inventory/material', label: 'Material entry', hint: 'When the Writer is away' },
                { path: '/inventory/stock', label: 'Stock on hand', hint: 'What is in the store now' },
                { path: '/inventory/ledger', label: 'Stock ledger', hint: 'Every entry, date by date' },
                { path: '/inventory/jumbo', label: 'Jumbo and rewinding', hint: 'Make jumbo rolls, rewind them' },
            ]
        },
        { label: 'Attendance', icon: 'shifts', path: '/hr/attendance' },
    ],
};

const isOn = (pathname, path) => pathname === path;
const tabOn = (pathname, tab) => (tab.path ? isOn(pathname, tab.path) : tab.items.some(i => isOn(pathname, i.path)));

const ROLE_NAME = { admin: 'Admin', writer: 'Writer', operator: 'Operator' };

const Layout = ({ children }) => {
    const { user, shift, logout, endShift } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [openMenu, setOpenMenu] = React.useState(null);   // label of the open ▾ group
    const [sheet, setSheet] = React.useState(null);         // phone: group shown in the bottom sheet
    const [showScrapModal, setShowScrapModal] = React.useState(false);

    const tabs = MENUS[user?.role] || MENUS.operator;

    const handleLogout = async (shouldEndShift) => {
        await logout(shouldEndShift);
        navigate('/login');
    };

    const handleLogoutClick = async () => {
        setOpenMenu(null);
        if (shift) {
            const end = await confirm({
                title: 'End your shift before logging out?',
                description: 'Your shift is still active. End it now, or log out and leave it running.',
                confirmText: 'End shift & log out',
                cancelText: 'Log out, keep shift',
            });
            handleLogout(end);
        } else {
            handleLogout(false);
        }
    };

    const handleEndShiftEarly = async () => {
        if (await confirm({ title: 'End your shift early?', description: "You'll record end-of-shift scrap next.", confirmText: 'End shift' })) {
            // Phase 4: capture end-of-shift scrap before the shift is closed.
            setShowScrapModal(true);
        }
    };

    const go = (path) => {
        setOpenMenu(null);
        setSheet(null);
        navigate(path);
    };

    const initials = (user?.fullName || '?').split(/\s+/).map(w => w.charAt(0)).join('').slice(0, 2).toUpperCase();
    const roleName = ROLE_NAME[user?.role] || user?.role;

    const tabCls = (on) => 'h-9 px-3.5 inline-flex items-center gap-1.5 rounded-full text-[13px] font-bold transition-colors whitespace-nowrap ' +
        (on ? 'bg-white/[0.13] text-white' : 'text-[#9FB3CC] hover:text-white hover:bg-white/[0.07]');

    const renderGroupList = (items) => (
        <div className="flex flex-col">
            {items.map(i => {
                const on = isOn(location.pathname, i.path);
                return (
                    <button key={i.path} onClick={() => go(i.path)} aria-current={on ? 'page' : undefined}
                        className={'text-left rounded-lg px-3 py-2 transition-colors ' + (on ? 'bg-[#E3ECFF]' : 'hover:bg-surface2')}>
                        <div className={'text-[13.5px] font-bold ' + (on ? 'text-accent-dark' : 'text-text-primary')}>{i.label}</div>
                        {i.hint && <div className="text-[12px] font-semibold text-text-muted">{i.hint}</div>}
                    </button>
                );
            })}
        </div>
    );

    return (
        <div className="min-h-screen flex flex-col bg-background text-text-primary font-sans">
            <ShiftMonitor />

            {showScrapModal && (
                <ScrapModal
                    onClose={() => setShowScrapModal(false)}
                    onComplete={endShift}
                />
            )}

            {/* ===== Navy top bar ===== */}
            <header className="sticky top-0 z-40 bg-navy text-white">
                <div className="h-[60px] max-w-[1400px] mx-auto flex items-center gap-6 px-4 md:px-7">
                    <button onClick={() => go(tabs[0].path || tabs[0].items[0].path)} className="flex items-center gap-2.5 text-[15px] font-extrabold text-white shrink-0" aria-label="FactoryDash home">
                        <span className="w-7 h-7 rounded-[9px] bg-accent flex items-center justify-center text-[14px]">F</span>
                        FactoryDash
                    </button>

                    <nav aria-label="Main" className="hidden md:flex items-center gap-1 min-w-0">
                        {tabs.map(tab => {
                            const on = tabOn(location.pathname, tab);
                            if (tab.path) {
                                return (
                                    <button key={tab.label} className={tabCls(on)} aria-current={on ? 'page' : undefined} onClick={() => go(tab.path)}>
                                        {tab.label}
                                    </button>
                                );
                            }
                            const open = openMenu === tab.label;
                            return (
                                <Popover key={tab.label} open={open} onOpenChange={o => setOpenMenu(o ? tab.label : null)}>
                                    <PopoverTrigger asChild>
                                        <button className={tabCls(on || open)}>
                                            {tab.label}
                                            <Icon name="chevron" className={'w-3 h-3 opacity-70 transition-transform duration-200 ' + (open ? 'rotate-180' : '')} />
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent align="start" sideOffset={10} className="w-72 p-1.5">
                                        {renderGroupList(tab.items)}
                                    </PopoverContent>
                                </Popover>
                            );
                        })}
                    </nav>

                    <Popover open={openMenu === '__me'} onOpenChange={o => setOpenMenu(o ? '__me' : null)}>
                        <PopoverTrigger asChild>
                            <button className="ml-auto flex items-center gap-2.5 rounded-full pl-1 pr-2 py-1 hover:bg-white/[0.07] transition-colors" aria-label="Your account">
                                <span className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-[12px] font-extrabold">{initials}</span>
                                <span className="hidden sm:block text-left leading-tight">
                                    <span className="block text-[12.5px] font-bold text-white max-w-[160px] truncate">{user?.fullName}</span>
                                    <span className="block text-[11px] font-semibold text-[#9FB3CC]">{roleName}</span>
                                </span>
                            </button>
                        </PopoverTrigger>
                        <PopoverContent align="end" sideOffset={10} className="w-56 p-1.5">
                            <div className="px-3 py-2 mb-1 border-b border-border">
                                <div className="text-[13.5px] font-bold text-text-primary truncate">{user?.fullName}</div>
                                <div className="text-[12px] font-semibold text-text-muted">{roleName}</div>
                            </div>
                            <button onClick={() => go('/guide')} className="w-full flex items-center gap-2.5 text-left rounded-lg px-3 py-2 text-[13.5px] font-bold text-text-primary hover:bg-surface2">
                                <Icon name="guide" className="w-4 h-4 text-text-muted" />Guide
                            </button>
                            <button onClick={handleLogoutClick} className="w-full flex items-center gap-2.5 text-left rounded-lg px-3 py-2 text-[13.5px] font-bold text-danger hover:bg-danger/10">
                                <Icon name="logout" className="w-4 h-4" />Log out
                            </button>
                        </PopoverContent>
                    </Popover>
                </div>
            </header>

            {shift && (
                <div className="bg-[#D7F3E8] text-[#0B6B4D]" role="status">
                    <div className="h-10 max-w-[1400px] mx-auto px-4 md:px-7 flex justify-between items-center text-[12.5px] font-bold gap-3">
                        <span className="flex items-center gap-2.5 min-w-0">
                            <span className="fd-live-dot text-success" aria-hidden="true" />
                            <span className="truncate">
                                <span className="capitalize">{shift.shift_type || shift.type}</span> shift{shift.isOt ? ' (OT)' : ''} running
                                <span className="font-semibold opacity-80 tnum"> since {new Date(shift.startTime || shift.start_time).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}</span>
                            </span>
                        </span>
                        <button onClick={handleEndShiftEarly} className="h-7 px-3 rounded-lg bg-white/70 hover:bg-white text-[#0B6B4D] font-bold transition-colors whitespace-nowrap">
                            End shift
                        </button>
                    </div>
                </div>
            )}

            <main id="main" className="flex-1 w-full max-w-[1400px] mx-auto px-4 pt-5 pb-28 md:px-7 md:pt-6 md:pb-10">
                {/* Pages are lazy-loaded (see App.js); show a spinner in the
                    content area only, so the top bar doesn't flash. */}
                <Suspense
                    fallback={
                        <div className="flex items-center justify-center py-24" role="status" aria-live="polite">
                            <span className="spinner text-accent" style={{ width: '1.25rem', height: '1.25rem' }} />
                        </div>
                    }
                >
                    <div className="fd-rise">{children}</div>
                </Suspense>
            </main>

            {/* ===== Phone: bottom tab bar ===== */}
            <nav aria-label="Main" className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card border-t border-border pb-[env(safe-area-inset-bottom)]">
                <div className="flex">
                    {tabs.map(tab => {
                        const on = tabOn(location.pathname, tab) || sheet === tab.label;
                        return (
                            <button key={tab.label}
                                onClick={() => (tab.path ? go(tab.path) : setSheet(sheet === tab.label ? null : tab.label))}
                                aria-current={on ? 'page' : undefined}
                                className={'flex-1 min-w-0 h-[60px] flex flex-col items-center justify-center gap-1 text-[11px] font-bold transition-colors ' + (on ? 'text-accent' : 'text-text-muted')}>
                                <Icon name={tab.icon} className="w-[22px] h-[22px]" />
                                <span className="truncate max-w-full px-1">{tab.short || tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </nav>

            {sheet && (
                <div className="md:hidden fixed inset-0 z-[35] flex flex-col justify-end">
                    <div className="absolute inset-0 bg-navy/40 animate-in fade-in duration-150" onClick={() => setSheet(null)} />
                    <div className="relative bg-card rounded-t-2xl p-3 pb-[calc(72px+env(safe-area-inset-bottom))] shadow-2xl animate-in slide-in-from-bottom duration-200">
                        <div className="flex items-center justify-between px-3 pb-2">
                            <span className="text-[15px] font-extrabold">{sheet}</span>
                            <button onClick={() => setSheet(null)} className="p-2 rounded-md text-text-muted hover:bg-surface2" aria-label="Close">
                                <Icon name="close" className="w-5 h-5" />
                            </button>
                        </div>
                        {renderGroupList(tabs.find(t => t.label === sheet)?.items || [])}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Layout;
